import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, MessagesSquare, RefreshCcw, Pause, Hand, Archive, Link as LinkIcon, ShieldOff, X } from 'lucide-react';
import { inviteApi } from '../../lib/inviteApi';
import InviteStatusBadge from '../../components/invite/InviteStatusBadge';

export default function ConversationsTab({ exhibitionId, selectedThreadId, setSelectedThreadId }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeId, setActiveId] = useState(selectedThreadId || null);
  const [showInboundModal, setShowInboundModal] = useState(false);

  const refreshThreads = async () => {
    setLoading(true);
    try {
      const d = await inviteApi.listThreads(exhibitionId);
      setThreads(d.threads || []);
      if (!activeId && d.threads?.[0]?.thread?.id) setActiveId(d.threads[0].thread.id);
    } finally { setLoading(false); }
  };

  const loadDetail = async (id) => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const d = await inviteApi.getThread(id);
      setDetail(d);
    } finally { setDetailLoading(false); }
  };

  useEffect(() => { refreshThreads(); /* eslint-disable-next-line */ }, [exhibitionId]);
  useEffect(() => { if (activeId) { loadDetail(activeId); setSelectedThreadId?.(activeId); } }, [activeId, setSelectedThreadId]);

  const generateOutreach = async () => {
    try {
      await inviteApi.generateOutreach(activeId);
      toast.success('Outreach drafts generated.');
      loadDetail(activeId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Generate failed'); }
  };
  const generateReply = async () => {
    try {
      await inviteApi.generateReply(activeId);
      toast.success('AI draft reply created.');
      loadDetail(activeId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Reply generation failed'); }
  };
  const pause = async () => { await inviteApi.pauseAgent(activeId); toast.success('Paused'); loadDetail(activeId); };
  const takeover = async () => { await inviteApi.takeoverAgent(activeId); toast.success('Manual takeover'); loadDetail(activeId); };
  const archive = async () => {
    await inviteApi.archiveThread(activeId);
    toast.success('Archived');
    refreshThreads();
    loadDetail(activeId);
  };

  const handleSend = async (mid) => {
    try {
      await inviteApi.sendMessage(mid);
      toast.success('Sent via Resend.');
      loadDetail(activeId);
      refreshThreads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Send failed'); }
  };

  const handleSavedMessage = async (mid, patch) => {
    try {
      await inviteApi.patchMessage(mid, patch);
      toast.success('Draft updated');
      loadDetail(activeId);
    } catch { toast.error('Update failed'); }
  };

  const handleSuppress = async () => {
    if (!detail?.contact?.email) return;
    try {
      await inviteApi.addSuppression({ email: detail.contact.email, reason: 'do_not_contact', source_contact_id: detail.contact.id, source_thread_id: activeId });
      toast.success('Added to suppression list.');
      loadDetail(activeId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Suppression failed'); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="conversations-tab">
      {/* Threads sidebar */}
      <div className="border border-[#1a1a1a] bg-[#0a0a0a] lg:col-span-1">
        <div className="px-3 py-2 border-b border-[#1a1a1a] flex items-center justify-between">
          <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#777]">Threads</p>
          <button onClick={refreshThreads} className="text-[#666] hover:text-[#90E0EF]"><RefreshCcw className="w-3.5 h-3.5"/></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {loading && <p className="p-4 text-xs font-mono text-[#555]">Loading…</p>}
          {!loading && !threads.length && <p className="p-4 text-xs font-mono text-[#555]">No threads.</p>}
          {threads.map((row) => {
            const t = row.thread; const c = row.contact || {};
            return (
              <button
                key={t.id}
                onClick={() => setActiveId(t.id)}
                data-clickable="true"
                data-testid={`thread-pick-${t.id}`}
                className={`block w-full text-left px-3 py-2.5 border-b border-[#0e0e0e] hover:bg-[#0c0c0c] ${activeId === t.id ? 'bg-[#0d0d0d] border-l-2 border-l-[#90E0EF]' : ''}`}
              >
                <p className="text-xs font-mono text-white truncate">{c.name || '—'}</p>
                <p className="text-[10px] font-mono text-[#888] truncate">{c.email || ''}</p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <InviteStatusBadge status={t.status} />
                  <InviteStatusBadge status={t.agent_mode} />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Center: contact + timeline */}
      <div className="lg:col-span-2 border border-[#1a1a1a] bg-[#0a0a0a]">
        {!activeId && <p className="p-6 text-xs font-mono text-[#555]">Pick a thread on the left.</p>}
        {activeId && detailLoading && <p className="p-6 text-xs font-mono text-[#555]">Loading thread…</p>}
        {detail && (
          <div className="flex flex-col h-full">
            <div className="px-5 py-3 border-b border-[#1a1a1a]">
              <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#90E0EF]">{detail.contact?.category || 'contact'}</p>
              <h3 className="font-outfit text-lg text-white tracking-tight">{detail.contact?.name || '—'}</h3>
              <p className="text-xs font-mono text-[#888]">{detail.contact?.email || ''} {detail.contact?.organization_name ? `· ${detail.contact.organization_name}` : ''}</p>
              <div className="flex items-center gap-1.5 mt-2">
                <InviteStatusBadge status={detail.thread.status} />
                <InviteStatusBadge status={detail.thread.agent_mode} />
                {detail.ticket && <InviteStatusBadge status={`ticket: ${detail.ticket.decision_status || 'pending'}`} />}
              </div>
            </div>

            <div className="px-5 py-4 space-y-3 overflow-y-auto max-h-[60vh]">
              {(!detail.messages || !detail.messages.length) && <p className="text-xs font-mono text-[#555]">No messages yet.</p>}
              {(detail.messages || []).map((m) => (
                <MessageCard key={m.id} m={m} onSend={() => handleSend(m.id)} onSave={(patch) => handleSavedMessage(m.id, patch)} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right: actions panel */}
      <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4 space-y-3" data-testid="conv-actions-panel">
        <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#777]">Actions</p>
        {!activeId && <p className="text-xs font-mono text-[#555]">Open a thread.</p>}
        {activeId && (
          <>
            <ActionBtn label="Generate outreach" icon={Sparkles} onClick={generateOutreach} testid="action-generate-outreach"/>
            <ActionBtn label="Generate reply" icon={MessagesSquare} onClick={generateReply} testid="action-generate-reply"/>
            <ActionBtn label="Add incoming reply" icon={Send} onClick={() => setShowInboundModal(true)} testid="action-add-inbound" tone="violet"/>
            <ActionBtn label="Pause agent" icon={Pause} onClick={pause} tone="muted"/>
            <ActionBtn label="Manual takeover" icon={Hand} onClick={takeover} tone="muted"/>
            <ActionBtn label="Add suppression" icon={ShieldOff} onClick={handleSuppress} tone="rose"/>
            <ActionBtn label="Archive thread" icon={Archive} onClick={archive} tone="muted"/>
            {detail?.thread && (
              <div className="pt-3 border-t border-[#1a1a1a]">
                <p className="text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">Ticket Tracking</p>
                <button onClick={async () => {
                  const u = await inviteApi.getTicketUrl(activeId);
                  await navigator.clipboard?.writeText(u.url).catch(()=>{});
                  toast.success('Tracking URL copied');
                }} data-clickable="true" data-testid="action-copy-ticket-url" className="inline-flex items-center gap-1.5 text-[10px] font-mono px-2.5 py-1.5 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 uppercase tracking-[0.14em]">
                  <LinkIcon className="w-3 h-3"/> Copy invite URL
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {showInboundModal && (
          <InboundModal onClose={() => setShowInboundModal(false)} onSubmit={async (payload) => {
            try {
              await inviteApi.manualInbound(activeId, payload);
              toast.success('Reply received & classified.');
              setShowInboundModal(false);
              loadDetail(activeId);
              refreshThreads();
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Failed');
            }
          }} />
        )}
      </AnimatePresence>
    </div>
  );
}

function ActionBtn({ label, icon: Icon, onClick, tone = 'cyan', testid }) {
  const map = {
    cyan: 'border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10',
    violet: 'border-[rgba(200,157,255,0.4)] text-[#c89dff] hover:bg-[rgba(200,157,255,0.07)]',
    rose: 'border-[rgba(232,139,160,0.4)] text-[#e88ba0] hover:bg-[rgba(232,139,160,0.06)]',
    muted: 'border-[#333] text-[#aaa] hover:text-white',
  };
  return (
    <button onClick={onClick} data-clickable="true" data-testid={testid} className={`w-full inline-flex items-center gap-2 text-[10px] font-mono px-3 py-2 border uppercase tracking-[0.14em] ${map[tone]}`}>
      <Icon className="w-3 h-3"/> {label}
    </button>
  );
}

function MessageCard({ m, onSend, onSave }) {
  const isInbound = m.direction === 'inbound';
  const isDraft = m.status === 'draft';
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(m.subject || '');
  const [body, setBody] = useState(m.body_text || '');

  return (
    <div className={`border ${isInbound ? 'border-[rgba(122,168,212,0.35)] bg-[rgba(122,168,212,0.04)]' : 'border-[#1a1a1a] bg-[#070707]'} p-3.5`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <InviteStatusBadge status={m.direction} />
          <InviteStatusBadge status={m.message_type} />
          <InviteStatusBadge status={m.status} />
        </div>
        <span className="text-[10px] font-mono text-[#555]">
          {(m.sent_at || m.received_at || m.created_at) ? new Date(m.sent_at || m.received_at || m.created_at).toLocaleString() : '—'}
        </span>
      </div>

      {!editing && <p className="text-xs font-mono text-[#ccc] mb-1.5">{m.subject || <span className="text-[#444]">(no subject)</span>}</p>}
      {editing && (
        <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid={`msg-edit-subject-${m.id}`} className="w-full bg-[#070707] border border-[#1f1f1f] text-white text-xs font-mono px-3 py-2 mb-2 focus:outline-none focus:border-[#90E0EF]" />
      )}

      {!editing && <p className="text-[12px] font-mono text-[#ddd] whitespace-pre-wrap leading-relaxed">{(m.body_text || '').slice(0, 700) || <span className="text-[#444]">(no body)</span>}{(m.body_text || '').length > 700 ? ' …' : ''}</p>}
      {editing && (
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} data-testid={`msg-edit-body-${m.id}`} className="w-full bg-[#070707] border border-[#1f1f1f] text-white text-xs font-mono px-3 py-2 focus:outline-none focus:border-[#90E0EF]" />
      )}

      {m.ai_summary && !editing && (
        <p className="mt-2 text-[10px] font-mono text-[#777] italic">AI summary · {m.ai_summary}</p>
      )}

      {m.direction === 'outbound' && (
        <div className="mt-3 pt-2 border-t border-[#161616] flex flex-wrap gap-2">
          {!editing && isDraft && (
            <>
              <button onClick={() => setEditing(true)} data-clickable="true" data-testid={`msg-edit-${m.id}`} className="text-[10px] font-mono px-2.5 py-1.5 border border-[#333] text-[#aaa] hover:text-white uppercase tracking-[0.14em]">Edit</button>
              <button onClick={onSend} data-clickable="true" data-testid={`msg-send-${m.id}`} className="text-[10px] font-mono px-2.5 py-1.5 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 uppercase tracking-[0.14em]">Send</button>
            </>
          )}
          {editing && (
            <>
              <button onClick={() => { setEditing(false); onSave({ subject, body_text: body }); }} data-clickable="true" data-testid={`msg-save-${m.id}`} className="text-[10px] font-mono px-2.5 py-1.5 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 uppercase tracking-[0.14em]">Save</button>
              <button onClick={() => { setEditing(false); setSubject(m.subject || ''); setBody(m.body_text || ''); }} data-clickable="true" className="text-[10px] font-mono px-2.5 py-1.5 border border-[#333] text-[#aaa] hover:text-white uppercase tracking-[0.14em]">Cancel</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function InboundModal({ onClose, onSubmit }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sender, setSender] = useState('');
  const submit = () => {
    if (!body.trim()) { toast.error('Paste the reply body.'); return; }
    onSubmit?.({ subject, body_text: body, sender_email: sender });
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-[#222] w-full max-w-2xl p-6 space-y-4" data-testid="inbound-modal">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#90E0EF]">Add Incoming Reply</p>
          <button onClick={onClose} className="text-[#777] hover:text-white" data-clickable="true"><X className="w-4 h-4"/></button>
        </div>
        <label className="block">
          <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">Sender email (optional)</span>
          <input value={sender} onChange={(e) => setSender(e.target.value)} data-testid="inbound-sender" className="w-full bg-[#070707] border border-[#1f1f1f] text-white text-xs font-mono px-3 py-2 focus:outline-none focus:border-[#90E0EF]" />
        </label>
        <label className="block">
          <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="inbound-subject" className="w-full bg-[#070707] border border-[#1f1f1f] text-white text-xs font-mono px-3 py-2 focus:outline-none focus:border-[#90E0EF]" />
        </label>
        <label className="block">
          <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">Body</span>
          <textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} data-testid="inbound-body" className="w-full bg-[#070707] border border-[#1f1f1f] text-white text-xs font-mono px-3 py-2 focus:outline-none focus:border-[#90E0EF]" />
        </label>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#1a1a1a]">
          <button onClick={onClose} className="text-[10px] font-mono px-3 py-2 border border-[#333] text-[#aaa] hover:text-white uppercase tracking-[0.14em]">Cancel</button>
          <button onClick={submit} data-clickable="true" data-testid="inbound-submit" className="text-[10px] font-mono px-3 py-2 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 uppercase tracking-[0.14em]">Classify reply</button>
        </div>
      </div>
    </motion.div>
  );
}
